import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Delete as DeleteIcon, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import SetupStepHelp from "./setup/SetupStepHelp";

const EtapaClientesNiveis = ({
  awarenessLevels,
  setAwarenessLevels,
  generateAwarenessLevels,
  company,
  setHasImportedAwarenessLevels,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("onboarding");
  const [loadingSugestao, setLoadingSugestao] = useState(false);
  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  const fetchAwarenessLevels = async () => {
    setLoadingSugestao(true);
    setHasImportedAwarenessLevels?.(true);
    try {
      const data = await generateAwarenessLevels(company, { mode: "hybrid" });
      setAwarenessLevels(data);
    } catch (error) {
      console.error("Erro ao gerar sugestões:", error);
    } finally {
      setLoadingSugestao(false);
    }
  };

  const handleEditLevel = (index, field, value) => {
    const updatedLevels = [...awarenessLevels];
    updatedLevels[index][field] = value;
    setAwarenessLevels(updatedLevels);
  };

  const handleAddLevel = () => {
    setAwarenessLevels([
      ...awarenessLevels,
      {
        title: t("setup.journeyStep.newTitle"),
        description: t("setup.journeyStep.newDescription"),
      },
    ]);
  };

  const handleDeleteLevel = (index) => {
    setAwarenessLevels(awarenessLevels.filter((_, idx) => idx !== index));
  };

  const handleMoveLevel = (index, direction) => {
    const updatedLevels = [...awarenessLevels];
    const levelToMove = updatedLevels[index];
    updatedLevels.splice(index, 1);
    updatedLevels.splice(index + direction, 0, levelToMove);
    setAwarenessLevels(updatedLevels);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <SetupStepHelp stepKey="clientsLevels" />
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: theme.palette.primary.main }}>
        {t("setup.journeyStep.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        {t("setup.journeyStep.intro")}
      </Typography>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <Button variant="contained" disabled={loadingSugestao} onClick={fetchAwarenessLevels}>
          {loadingSugestao ? <CircularProgress size={24} /> : t("setup.journeyStep.generateAi")}
        </Button>
      </Box>

      <Grid container spacing={2} mb={2}>
        {awarenessLevels.map((level, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("setup.journeyStep.titleLabel")}
                    value={level.title}
                    onChange={(e) => handleEditLevel(index, "title", e.target.value)}
                  />
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t("setup.journeyStep.descriptionLabel")}
                  value={level.description}
                  onChange={(e) => handleEditLevel(index, "description", e.target.value)}
                />
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <IconButton onClick={() => handleMoveLevel(index, -1)} disabled={index === 0} aria-label="up">
                    <ArrowUpward />
                  </IconButton>
                  <IconButton
                    onClick={() => handleMoveLevel(index, 1)}
                    disabled={index === awarenessLevels.length - 1}
                    aria-label="down"
                  >
                    <ArrowDownward />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteLevel(index)} aria-label="delete">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined" sx={{ height: "100%", cursor: "pointer" }} onClick={handleAddLevel}>
            <CardContent sx={{ height: "100%" }}>
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "100%", minHeight: 120 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: theme.palette.primary.main }}>
                  {t("setup.journeyStep.addLevel")}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EtapaClientesNiveis;
